import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User } from '../../types';
import { apiService } from '../../services/api';
import { useToast } from '../ui/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface OvertimeRecord {
  id: number;
  userId: number;
  userName: string;
  date: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  breakMinutes: number;
  reason?: string;
}

interface OvertimeApprovalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  overtimeRecord: OvertimeRecord | null;
  approvers: User[];
  currentUser: User;
  onApprovalComplete: (approved: boolean, overtimeId: number) => void;
}

export const OvertimeApprovalDialog: React.FC<OvertimeApprovalDialogProps> = ({
  isOpen,
  onClose,
  overtimeRecord,
  approvers,
  currentUser,
  onApprovalComplete,
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [overtimeReason, setOvertimeReason] = useState('');
  const [selectedApproverId, setSelectedApproverId] = useState<string>(currentUser.id.toString());
  const { toast } = useToast();

  const handleApproval = async (isApproved: boolean) => {
    if (!overtimeRecord) return;

    // Validate required fields for approval
    if (isApproved && !overtimeReason.trim()) {
      toast({
        title: 'Eroare',
        description: 'Motivul pentru ore suplimentare este obligatoriu pentru aprobare',
        variant: 'destructive',
      });
      return;
    }

    if (!approvalComment.trim()) {
      toast({
        title: 'Eroare',
        description: isApproved 
          ? 'Detaliile aprobării sunt obligatorii' 
          : 'Motivul respingerii este obligatoriu',
        variant: 'destructive',
      });
      return;
    }

    setIsApproving(true);
    try {
      const fullComment = isApproved 
        ? `MOTIV OVERTIME: ${overtimeReason}\n\nDETALII APROBARE: ${approvalComment}`
        : `MOTIV RESPINGERE: ${approvalComment}`;
        
      await apiService.approveOvertime(overtimeRecord.id, {
        isApproved,
        approvedById: parseInt(selectedApproverId),
        comment: fullComment,
      });

      toast({
        title: isApproved ? 'Ore suplimentare aprobate' : 'Ore suplimentare respinse',
        description: `Orele suplimentare pentru ${overtimeRecord.userName} au fost ${
          isApproved ? 'aprobate' : 'respinse'
        }.`,
        variant: isApproved ? 'default' : 'destructive',
      });

      onApprovalComplete(isApproved, overtimeRecord.id);
      onClose();
      setApprovalComment('');
      setOvertimeReason('');
    } catch (error) {
      console.error('Error approving overtime:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut procesa orele suplimentare',
        variant: 'destructive',
      });
    } finally {
      setIsApproving(false);
    }
  };

  if (!overtimeRecord) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Aprobare Ore Suplimentare</DialogTitle>
          <DialogDescription>
            Verificați și aprobați orele suplimentare pentru {overtimeRecord.userName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Muncitor</Label>
              <p className="font-medium">{overtimeRecord.userName}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Data</Label>
              <p className="font-medium">{overtimeRecord.date}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Ore normale</Label>
              <p className="font-medium">{overtimeRecord.regularHours.toFixed(2)}h</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Ore suplimentare</Label>
              <p className="font-medium text-orange-600">
                {overtimeRecord.overtimeHours.toFixed(2)}h
              </p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Total ore</Label>
              <p className="font-medium">{overtimeRecord.totalHours.toFixed(2)}h</p>
            </div>
          </div>

          {overtimeRecord.reason && (
            <div>
              <Label className="text-sm text-gray-600">Motiv</Label>
              <p className="text-sm">{overtimeRecord.reason}</p>
            </div>
          )}

          <div>
            <Label htmlFor="approver">Aprobat de</Label>
            <Select value={selectedApproverId} onValueChange={setSelectedApproverId}>
              <SelectTrigger id="approver">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {approvers.map((approver) => (
                  <SelectItem key={approver.id} value={approver.id.toString()}>
                    {approver.name} ({approver.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reason" className="flex items-center gap-1">
                Motivul orelor suplimentare
                <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500">(obligatoriu pentru aprobare)</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="Ex: Termen limită urgent pentru proiect, Înlocuire coleg absent, Comandă specială client, etc."
                value={overtimeReason}
                onChange={(e) => setOvertimeReason(e.target.value)}
                className="min-h-[60px]"
                disabled={overtimeRecord?.reason ? true : false}
                defaultValue={overtimeRecord?.reason || ''}
              />
              {overtimeRecord?.reason && (
                <p className="text-sm text-gray-600 mt-1">Motiv existent: {overtimeRecord.reason}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="comment" className="flex items-center gap-1">
                {isApproving ? 'Detalii aprobare' : 'Comentariu decizie'}
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="comment"
                placeholder={isApproving 
                  ? "Detaliați de ce aprobați aceste ore suplimentare..." 
                  : "Explicați decizia de aprobare sau respingere..."}
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isApproving}
          >
            Anulează
          </Button>
          <Button
            variant="destructive"
            onClick={() => handleApproval(false)}
            disabled={isApproving}
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            Respinge
          </Button>
          <Button
            variant="default"
            onClick={() => handleApproval(true)}
            disabled={isApproving}
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Aprobă
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};